// src/features/files/components/RenameFileDialog.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useFiles } from '../hooks/useFiles';

export default function RenameFileDialog({ open, onClose, file, onSuccess }) {
  const { t } = useTranslation(['files', 'common']);
  const { updateFileMetadata, loading } = useFiles();
  const [error, setError] = useState(null);
  const [filename, setFilename] = useState('');
  const [extension, setExtension] = useState('');
  const inputRef = useRef(null);

  // Update filename when file changes
  useEffect(() => {
    if (file) {
      const fullName = file.filename || '';
      const lastDotIndex = fullName.lastIndexOf('.');
      
      if (lastDotIndex > 0) {
        setFilename(fullName.substring(0, lastDotIndex));
        setExtension(fullName.substring(lastDotIndex));
      } else {
        setFilename(fullName);
        setExtension('');
      }
    }
  }, [file]);

  // Auto-select text when dialog opens
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.select();
        }
      }, 100);
    }
  }, [open]);

  const handleChange = (event) => {
    setFilename(event.target.value);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!filename.trim()) {
      setError(t('files:errors.filename_required'));
      return;
    }

    if (!file?.file_id) {
      setError(t('common:errors.generic'));
      return;
    }

    const result = await updateFileMetadata(file.file_id, {
      filename: filename.trim() + extension,
    });

    if (result?.success) {
      onSuccess?.();
      onClose();
    } else {
      setError(result?.error || t('common:errors.generic'));
    }
  };

  const handleClose = () => {
    if (!loading) {
      const fullName = file?.filename || '';
      const lastDotIndex = fullName.lastIndexOf('.');
      
      if (lastDotIndex > 0) {
        setFilename(fullName.substring(0, lastDotIndex));
        setExtension(fullName.substring(lastDotIndex));
      } else {
        setFilename(fullName);
        setExtension('');
      }
      
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{t('files:rename_file')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            <TextField
              label={t('files:file_name')}
              value={filename}
              onChange={handleChange}
              required
              fullWidth
              autoFocus
              disabled={loading}
              inputRef={inputRef}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            {t('common:buttons.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !filename.trim()}
          >
            {loading ? <CircularProgress size={24} /> : t('common:buttons.rename')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}